import { useState, useCallback } from 'react';

export interface ConfirmationConfig {
  title?: string;
  message?: string;
  recordName?: string;
  recordId?: string;
  deleteButtonText?: string;
  // Optional UI customizations
  variant?: 'delete' | 'cancel' | 'warning' | 'info';
  cancelButtonText?: string;
  onConfirm: () => void | Promise<void>;
}

export interface ConfirmationState extends ConfirmationConfig {
  isOpen: boolean;
  isLoading: boolean;
}

export const useConfirmation = () => {
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(null);

  const showConfirmation = useCallback((config: ConfirmationConfig) => {
    setConfirmation({
      ...config,
      isOpen: true,
      isLoading: false,
    });
  }, []);

  const hideConfirmation = useCallback(() => {
    setConfirmation(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!confirmation?.onConfirm) return;

    try {
      // Set loading state
      setConfirmation(prev => prev ? { ...prev, isLoading: true } : null);
      
      // Execute the confirmation action
      await confirmation.onConfirm();
      
      // Close the modal after successful action
      hideConfirmation();
    } catch (error) {
      // Reset loading state on error, keep modal open
      setConfirmation(prev => prev ? { ...prev, isLoading: false } : null);
      // Error handling should be done in the onConfirm function
      console.error('Confirmation action failed:', error);
    }
  }, [confirmation?.onConfirm, hideConfirmation]);

  return {
    confirmation,
    showConfirmation,
    hideConfirmation,
    handleConfirm,
  };
};